import { todayStr, toLocalDateStr } from "../../lib/date";
import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getStudentAssessmentHistory } from "../../lib/api";
import api from "../../lib/api";

const CATS = [
  { key: "participation_level", label: "Attend" },
  { key: "engagement", label: "Engage" },
  { key: "communication", label: "Comm" },
  { key: "behavior", label: "Behav" },
  { key: "attention", label: "Attn" },
  { key: "work_completion", label: "Task" },
];

export function StudentAssessmentTab({ studentId }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("all");
  const [comparison, setComparison] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const end = todayStr();
      const start = dateRange === "all" ? undefined : toLocalDateStr(new Date(Date.now() - parseInt(dateRange) * 86400000));
      const res = await getStudentAssessmentHistory(studentId, { start_date: start, end_date: end });
      setHistory(res.data);
      try {
        const compRes = await api.get(`/self-assessment/comparison/${studentId}`);
        setComparison(compRes.data);
      } catch {}
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [studentId, dateRange]);

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading assessment data...</p>;
  if (!history || history.total_entries === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]" data-testid="assessment-date-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-center py-8 text-muted-foreground" data-testid="assessment-empty">No assessment data found for this range. Try "All time".</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[180px]" data-testid="assessment-date-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{history.total_entries} entries</span>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${history.dna_entries > 0 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-400"}`}
          data-testid="student-dna-count"
          title="Did Not Attend records in this period"
        >
          {history.dna_entries} DNA
        </span>
      </div>

      {/* Averages */}
      {history.averages && (
        <div className="grid grid-cols-6 gap-2">
          {CATS.map(c => {
            const v = history.averages[c.key];
            const color = !v ? "bg-gray-100" : v >= 3 ? "bg-green-100 text-green-800" : v >= 2 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800";
            return (
              <div key={c.key} className={`p-3 rounded-lg text-center ${color}`} data-testid={`avg-${c.key}`}>
                <div className="text-lg font-bold">{v ? v.toFixed(1) : "-"}</div>
                <div className="text-xs">{c.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Weekly Trends Chart */}
      {history.assessments.length >= 2 && (() => {
        const weekMap = {};
        history.assessments.filter(a => !a.did_not_attend).forEach(a => {
          const d = new Date(a.date + "T00:00:00");
          const dayOfWeek = d.getDay();
          const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
          const weekStart = new Date(d.setDate(diff));
          const key = toLocalDateStr(weekStart);
          if (!weekMap[key]) weekMap[key] = [];
          weekMap[key].push(a);
        });
        const chartData = Object.entries(weekMap)
          .sort(([a],[b]) => a.localeCompare(b))
          .map(([week, entries]) => {
            const row = { week: `Wk ${new Date(week+"T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}` };
            CATS.forEach(c => {
              const vals = entries.map(e => e[c.key]).filter(v => v != null);
              row[c.key] = vals.length ? +(vals.reduce((a,b) => a+b, 0) / vals.length).toFixed(2) : null;
            });
            return row;
          });
        if (chartData.length < 2) return null;
        const colors = ["#2563eb","#16a34a","#d97706","#dc2626","#7c3aed","#0891b2"];
        return (
          <div className="border rounded-lg p-4" data-testid="weekly-trends-chart">
            <h3 className="font-semibold mb-3 text-sm">Weekly Assessment Trends</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 4]} ticks={[1,2,3,4]} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {CATS.map((c, i) => (
                  <Line key={c.key} type="monotone" dataKey={c.key} name={c.label}
                    stroke={colors[i]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* History table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0d2240] text-white">
              <th className="px-3 py-2 text-left font-semibold">Date</th>
              <th className="px-2 py-2 text-left font-semibold">Class</th>
              <th className="px-2 py-2 text-left font-semibold">Group</th>
              {CATS.map(c => <th key={c.key} className="px-2 py-2 text-center font-semibold text-xs">{c.label}</th>)}
              <th className="px-2 py-2 text-center font-semibold text-xs">DNA</th>
              <th className="px-2 py-2 text-left font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {history.assessments.map((a, i) => {
              const cellColor = (v) => !v ? "" : v >= 3 ? "text-green-700 font-semibold" : v === 2 ? "text-yellow-700" : "text-red-700 font-semibold";
              return (
                <tr key={a.id || i} className={`border-b ${a.did_not_attend ? "bg-orange-50/50" : ""}`}>
                  <td className="px-3 py-1.5 text-xs">{a.date}</td>
                  <td className="px-2 py-1.5 text-xs">{a.class_name}</td>
                  <td className="px-2 py-1.5 text-xs">{a.group_type}</td>
                  {CATS.map(c => (
                    <td key={c.key} className={`px-2 py-1.5 text-center text-xs ${cellColor(a[c.key])}`}>
                      {a[c.key] || "-"}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center text-xs">{a.did_not_attend ? <span className="inline-block bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded">DNA</span> : ""}</td>
                  <td className="px-2 py-1.5 text-xs truncate max-w-[150px]">{a.notes || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Self-Assessment Comparison */}
      {comparison && comparison.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              Teacher vs. Student Self-Assessment
            </CardTitle>
            <CardDescription>Side-by-side comparison of teacher scores and student self-ratings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="comparison-table">
                <thead>
                  <tr className="bg-[#0d2240] text-white">
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Block</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Attend</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Engage</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Comm</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Behav</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Attn</th>
                    <th className="px-2 py-2 text-center font-semibold" colSpan={2}>Task</th>
                  </tr>
                  <tr className="bg-gray-100 text-xs">
                    <th></th><th></th>
                    {CATS.map(c => (
                      <React.Fragment key={c.key}>
                        <th className="px-1 py-1 text-center text-blue-700">T</th>
                        <th className="px-1 py-1 text-center text-purple-700">S</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.slice(0, 20).map((row, i) => {
                    const cellColor = (v) => !v ? "" : v >= 3 ? "text-green-700 font-semibold" : v === 2 ? "text-yellow-700" : "text-red-700 font-semibold";
                    const diffBg = (tv, sv) => {
                      if (tv == null || sv == null) return "";
                      const diff = Math.abs(tv - sv);
                      return diff >= 2 ? "bg-red-50" : diff === 1 ? "bg-yellow-50" : "bg-green-50";
                    };
                    return (
                      <tr key={`${row.date}-${row.block}-${i}`} className="border-b">
                        <td className="px-3 py-1.5">{row.date}</td>
                        <td className="px-2 py-1.5">{row.block}</td>
                        {CATS.map(c => (
                          <React.Fragment key={c.key}>
                            <td className={`px-1 py-1.5 text-center ${cellColor(row.teacher?.[c.key])} ${diffBg(row.teacher?.[c.key], row.student?.[c.key])}`}>
                              {row.teacher?.[c.key] || "-"}
                            </td>
                            <td className={`px-1 py-1.5 text-center ${cellColor(row.student?.[c.key])} ${diffBg(row.teacher?.[c.key], row.student?.[c.key])}`}>
                              {row.student?.[c.key] || "-"}
                            </td>
                          </React.Fragment>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span><span className="font-semibold text-blue-700">T</span> = Teacher</span>
              <span><span className="font-semibold text-purple-700">S</span> = Student Self-Rating</span>
              <span className="inline-block w-3 h-3 bg-green-50 border rounded"></span> Match
              <span className="inline-block w-3 h-3 bg-yellow-50 border rounded"></span> Off by 1
              <span className="inline-block w-3 h-3 bg-red-50 border rounded"></span> Off by 2+
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
